/**
 * The pending access-request count, as context (#2496, D25).
 *
 * Two consumers, and neither can be handed a prop for the same reason `availability-summary-context`
 * exists: `AppHostFrame` builds the nav rail's items and `PeoplePane` is instantiated by
 * `AppLoader` from a manifest `loader` typed `() => Promise<{ default: ComponentType }>`, which
 * passes a pane nothing at all. One read feeds both.
 *
 * Shaped like `availability-summary-context.tsx`: the composition root runs the fetch and holds the
 * state, and this module is a value carrier with no effect of its own.
 *
 * ## ⛔ IT CARRIES A `refresh`, WHICH THE AVAILABILITY SIBLING DOES NOT
 *
 * The availability summary is a property of the FLEET and nothing in the console changes it, so a
 * consumer that could re-read it would only be re-reading the same number. This count is a property
 * of a QUEUE that the People section itself empties — approve or deny a row and the rail is wrong
 * by one until the next shell mount. ⇒ the pane that performs the write tells the count to re-read,
 * and it is the only caller. ⚠️ Elsewhere the count is as fresh as the last shell mount, which is
 * stated on the section rather than hidden: a rail count is an invitation to look, not a ledger.
 *
 * ## ⛔ WHY THE COUNT IS NOT `DashboardOverview.pendingAccessRequests` (D25)
 *
 * That field exists and this console renders it nowhere. `TenantsPane` is the one consumer of the
 * overview and is gated `cp:tenant:read`, held by `platform_viewer` — who cannot open People. A
 * count sourced there would be visible to a principal with no door to the queue and invisible to
 * the administrator who has one. `GET /v1/admin/access-requests/summary` shares the People item's
 * own `cp:operator:read` gate, which is the whole reason it is a route rather than a field.
 */
import { createContext, useContext, type ReactNode } from 'react';
import type { AccessRequestSummary } from '../api/accessRequests.api';
import type { AsyncState } from '../panes/async-state';

/**
 * What the provider carries.
 *
 * ⛔ ONE OBJECT, NOT TWO CONTEXTS. The state and the way to re-read it are used together by the one
 * component that writes, and splitting them would let a consumer subscribe to a `refresh` for a
 * state it cannot see.
 */
export interface AccessRequestSummaryValue {
  readonly state: AsyncState<AccessRequestSummary>;
  /** ⛔ Re-issues the summary read. Called by `PeoplePane` after an approve or a deny, and nowhere else. */
  readonly refresh: () => void;
}

const AccessRequestSummaryCtx = createContext<AccessRequestSummaryValue | null>(null);

/**
 * The state a reader whose `cp:operator:read` pre-check failed should see.
 *
 * ⛔ **A 403, NOT A `loading` THAT NEVER SETTLES.** The gate this console applies and the gate the
 * server applies are one fact about one reader (R-030), so both routes to *"not yours"* must reach
 * the same arm — `isForbidden` is what recognises it, and `PeoplePane` renders a `ForbiddenState`
 * from it either way. Leaving such a reader at `loading` would put a spinner on a read that will
 * never be issued.
 *
 * ⛔ Exported and returned BY REFERENCE from {@link visibleAccessRequestSummary}, so a consumer
 * memoising on identity does not churn — `SUMMARY_FORBIDDEN`'s rule in the availability sibling.
 *
 * ⚠️ The sentence is the console's own. It is never rendered as a producer message, because no
 * producer sent it: this arm is reached without a request being made.
 */
export const ACCESS_REQUEST_SUMMARY_FORBIDDEN: AsyncState<AccessRequestSummary> = {
  kind: 'error',
  status: 403,
  message: 'Reading access requests requires cp:operator:read.',
};

/** The state before the one read settles. The only arm that will change on its own. */
export const ACCESS_REQUEST_SUMMARY_LOADING: AsyncState<AccessRequestSummary> = { kind: 'loading' };

/**
 * Carries the root's summary state to the whole authenticated subtree.
 *
 * Value-only, like `AvailabilitySummaryProvider`: the fetch stays in `CpAdminApp`, which is the only
 * component in this package holding mount-time effects. ⛔ Mounted INSIDE `PermissionProvider` and
 * above `AuthenticatedShell`, because the value was derived from a site-wide key the snapshot also
 * carries and a provider must not sit above the thing it agrees with.
 */
export function AccessRequestSummaryProvider({
  value,
  children,
}: {
  readonly value: AccessRequestSummaryValue;
  readonly children: ReactNode;
}): React.JSX.Element {
  return <AccessRequestSummaryCtx value={value}>{children}</AccessRequestSummaryCtx>;
}

/**
 * The one summary value.
 *
 * ⛔ Throws outside a provider rather than defaulting, on `useAvailabilitySummary`'s rule: a default
 * of `{ kind: 'loading' }` would hand a subtree nobody told anything a spinner that never resolves,
 * and a default `refresh` that did nothing would make `PeoplePane`'s post-write re-read silently
 * absent — a count wrong by one, with no test able to see the difference.
 */
export function useAccessRequestSummary(): AccessRequestSummaryValue {
  const ctx = useContext(AccessRequestSummaryCtx);
  if (ctx === null) {
    throw new Error(
      'useAccessRequestSummary must be used within an AccessRequestSummaryProvider',
    );
  }
  return ctx;
}

/**
 * Which summary state may be shown, derived during render from the one site-wide pre-check and
 * whatever has been fetched.
 *
 * ⛔ **A RENDER-TIME DERIVATION, AND IT MUST STAY ONE.** `visibleGrantsState`'s docblock states the
 * reason with its measurement: React flushes passive effects after the commit, so a reset written in
 * an effect always leaves one commit holding the previous value. Here that matters because an
 * operator who loses `cp:operator:read` must read the refusal on the render that loses it, not one
 * commit later with the previous number still in the chrome.
 *
 * ⚠️ **THE PRE-CHECK SAVES A REQUEST; IT AUTHORIZES NOTHING.** The server's
 * `@RequirePermission('cp:operator:read')` is the gate. A principal whose snapshot says yes and
 * whose server says no renders the same absence, because both reach an `error` with status 403.
 */
export function visibleAccessRequestSummary(
  canReadOperators: boolean,
  fetched: AsyncState<AccessRequestSummary> | null,
): AsyncState<AccessRequestSummary> {
  if (!canReadOperators) return ACCESS_REQUEST_SUMMARY_FORBIDDEN;
  return fetched ?? ACCESS_REQUEST_SUMMARY_LOADING;
}

/**
 * The rail count, or `null`.
 *
 * ⛔ **`null` FOR EVERY ARM THAT IS NOT A `ready` NON-ZERO**, so in-flight, failed, forbidden and a
 * collected zero all produce the same rail. The collected zero is deliberately in that bucket: a
 * rail count exists to draw the eye to something, and an empty queue has nothing to draw it to. The
 * People section is the surface that states each absence in a sentence, which a two-line rail item
 * has no room for.
 *
 * ⛔ Exported and derived HERE rather than inline in `AppHostFrame`, so the rule has a unit test of
 * its own: `AppHost`'s own arm is exercised end to end by one row of `availability-summary.test.tsx`,
 * and one row cannot reach five arms.
 */
export function pendingAccessRequestCount(state: AsyncState<AccessRequestSummary>): number | null {
  return state.kind === 'ready' && state.data.pending > 0 ? state.data.pending : null;
}
